import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ENTITY_REGISTRY, EntityConfig, EntityKey } from '../constants/entity-registry';

/**
 * Interface for API error responses with custom error codes
 */
interface ApiErrorResponse {
  statusCode?: number;
  error?: string;
  message?: string;
  code?: string;
}

/**
 * Service for building localized success and error messages
 * Uses EntityRegistry for entity-specific messages
 */
@Injectable({ providedIn: 'root' })
export class MessageBuilderService {
  /**
   * Build a success message based on HTTP method and entity
   * @param method - HTTP method (POST, PUT, PATCH, DELETE)
   * @param entityKey - Entity key from registry
   * @returns Localized success message
   */
  buildSuccessMessage(method: string, entityKey: EntityKey | null): string {
    if (!entityKey || !ENTITY_REGISTRY[entityKey]) {
      return 'Operazione completata con successo';
    }

    const entity = ENTITY_REGISTRY[entityKey];
    switch (method) {
      case 'POST':
        return entity.messages.success.created;
      case 'PUT':
      case 'PATCH':
        return entity.messages.success.updated;
      case 'DELETE':
        return entity.messages.success.deleted;
      default:
        return 'Operazione completata con successo';
    }
  }

  /**
   * Build an error message based on HTTP error and entity
   * Supports custom error codes from backend
   * @param error - HTTP error response
   * @param entityKey - Entity key from registry
   * @returns Localized error message
   */
  buildErrorMessage(error: HttpErrorResponse, entityKey: EntityKey | null): string {
    const status = error.status;
    const errorBody = error.error as ApiErrorResponse | undefined;
    const errorCode = errorBody?.code;

    if (entityKey && ENTITY_REGISTRY[entityKey]) {
      const entity = ENTITY_REGISTRY[entityKey] as EntityConfig;

      // Check for custom error code first
      if (errorCode && entity.messages.error.custom?.[errorCode]) {
        return entity.messages.error.custom[errorCode];
      }

      // Fall back to standard error messages
      if (status === 404) return entity.messages.error.notFound;
      if (status === 409) return entity.messages.error.conflict;
      if (status === 400 || status === 422) return entity.messages.error.validation;
    }

    // Generic error messages
    switch (status) {
      case 0:
        return 'Errore di connessione. Verifica la tua connessione internet.';
      case 401:
        return 'Sessione scaduta. Effettua nuovamente il login.';
      case 403:
        return 'Non hai i permessi per eseguire questa operazione.';
      case 404:
        return 'Risorsa non trovata.';
      case 409:
        return 'Conflitto: la risorsa esiste già.';
      case 500:
        return 'Errore del server. Riprova più tardi.';
      default:
        return 'Si è verificato un errore. Riprova.';
    }
  }

  /**
   * Check if an entity key is registered
   * @param key - Key to check
   * @returns True if entity is registered
   */
  isEntityRegistered(key: string): key is EntityKey {
    return key in ENTITY_REGISTRY;
  }
}
